import { cn } from "@/shared/util/cn";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  Accordion as AccordionRoot,
  AccordionTrigger,
} from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";

interface AccordionProps {
  className?: string;
  children?: React.ReactNode;
  /**
   * If true, the accordion is expanded by default. Defaults to false (collapsed).
   */
  defaultOpen?: boolean;
}

const Accordion = ({ className, children, defaultOpen = false }: AccordionProps) => (
  <AccordionRoot
    type="single"
    collapsible
    defaultValue={defaultOpen ? "item" : undefined}
    className={cn("w-full shadow-none", className)}
  >
    <AccordionItem value="item" className={cn("border-b border-[var(--color-mockup-border)]")}>
      {children}
    </AccordionItem>
  </AccordionRoot>
);
Accordion.displayName = "Accordion";

interface AccordionSummaryProps extends React.ComponentPropsWithoutRef<typeof AccordionTrigger> {
  expandIcon?: React.ReactNode;
  actions?: React.ReactNode;
}

const AccordionSummary = React.forwardRef<
  React.ElementRef<typeof AccordionTrigger>,
  AccordionSummaryProps
>(({ className, children, expandIcon, actions, ...props }, ref) => (
  <AccordionHeader className="flex w-full">
    <AccordionTrigger
      ref={ref}
      className={cn(
        "flex w-full min-w-0 items-center gap-2 px-4 py-3 text-left font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] [&[data-state=open]_.accordion-chevron]:rotate-180",
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
        {children}
      </div>
      {actions ? (
        <div
          className="flex shrink-0 items-center gap-0.5"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      ) : null}
      {expandIcon !== undefined ? (
        expandIcon
      ) : (
        <ChevronDown className="accordion-chevron h-4 w-4 shrink-0 text-[var(--color-mockup-text-light)] transition-transform duration-200" />
      )}
    </AccordionTrigger>
  </AccordionHeader>
));
AccordionSummary.displayName = "AccordionSummary";

const AccordionDetails = React.forwardRef<
  React.ElementRef<typeof AccordionContent>,
  React.ComponentPropsWithoutRef<typeof AccordionContent>
>(({ className, ...props }, ref) => (
  <AccordionContent ref={ref} className={cn("overflow-hidden", className)} {...props} />
));
AccordionDetails.displayName = "AccordionDetails";

const AccordionActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-wrap items-center gap-2 px-4 pb-3", className)} {...props} />
  )
);
AccordionActions.displayName = "AccordionActions";

export { Accordion, AccordionActions, AccordionDetails, AccordionSummary };

